import Modal from "@/common/elements/Modal";
import AddCharges, { MODE } from "../AddCharges";
import { useEffect, useRef, useState } from "react";
import {
  getBookingsByNumberUtil,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { setBookings, setLastBookingsFromAPI } from "@/slices/bookingsSlice";
import { v4 as uuidV4 } from "uuid";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import {
  saveExtraTableRecord,
  saveBioAndArrestReportsExtra,
  saveInmatePropertyExtra,
  saveInmateMedicalExtra,
  saveCashAccountingNewExtra,
  saveCashAccountingNewTransactionsExtra,
  saveBookingBailsExtra,
  saveHoldsExtra,
  saveInmateMovementRulesExtra,
  saveInmateTrackingExtra,
  saveInmatePropertyItemsExtra,
  saveAppointmentLogExtra,
  saveBioAddressesExtra,
  saveBusinessesExtra,
  saveScheduledEventsExtra,
  saveMedicationLogExtra,
  saveVictimNotificationExtra,
  saveVictimNotificationLogExtra,
} from "@/util/extraColumnActions";
import notificationService from "@/util/notificationService";
import useDialog from "@/hooks/useDialog";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import {
  getTriggeringEntriesFromDiff,
  buildTransportPayloads,
  createTransportsForNewMovements,
} from "@/util/transportAutoCreate";

const EditChargesModal = ({
  show,
  onHide,
  isEdit,
  selectChargeData = null,
  // Sticky-field values to pre-fill on a brand-new charge (see PCI's "Add
  // Charge Based on Selected"). Kept separate from selectChargeData: only
  // selectChargeData drives the create-vs-update decision in onSaveChanges,
  // so a new charge stays a new charge even though some fields are prefilled.
  stickyDefaults = null,
  chargesData = [],
  bookingBails,
  startingPoint,
}) => {
  const dispatch = useDispatch();
  const { bookings, irisSearchId, irisEnrolleId } = useSelector(
    (state) => state?.bookings,
  );
  const chargeFormRef = useRef();
  const [user, setUser] = useState(null);
  const [isAPICall, setisAPICall] = useState(false);
  const [courtListsData, setCourtListsData] = useState([]);
  const [isDisabled, setDisabled] = useState(false);
  const fullSearch = useSelector((state) => state.fullSearch);
  const { showDialog } = useDialog();

  const notifyIfAllChargesCleared = (charges, inJail) => {
    const allCleared =
      isNonEmptyVariable(charges) &&
      charges.length > 0 &&
      charges.every((c) => c?.clearedToGo === true);

    if (allCleared && inJail === true) {
      showDialog("info", {
        title: "",
        content:
          "All charges have been cleared, which should mean that there is no longer " +
          "any reason to hold this inmate based on their charge(s).",
        confirmButtonText: "Ok",
      });
    }
  };
  useEffect(() => {
    const loginUser = JSON.parse(localStorage.getItem("userDetails"));
    if (isNonEmptyVariable(loginUser)) {
      setUser(loginUser);
    }
  }, []);

  useEffect(() => {
    // Only reset to a truly blank form when there's nothing to prefill with.
    // A sticky-field "Add Based on Selected" charge has selectChargeData
    // = null (it's a new record, not an edit) but stickyDefaults populated
    // — without this check, this reset was firing after AddCharges already
    // applied stickyDefaults to the form (child effects run before parent
    // effects on mount), wiping the prefilled values back to blank.
    if (
      !isNonEmptyVariable(selectChargeData) &&
      !isNonEmptyVariable(stickyDefaults)
    ) {
      chargeFormRef?.current?.form?.resetFields();
    }
  }, [selectChargeData, stickyDefaults]);

  useEffect(() => {
    // A brand-new "Add Charge Based on Selected" charge has no
    // selectChargeData (it's not an edit), but a sticky field that lives
    // on the nested court[] array (e.g. Court_Name_ID) arrives via
    // stickyDefaults.court instead — see addChargeBasedOnSelected in
    // ChargesList/index.jsx. Fall back to that so the new charge's Court
    // Dates grid gets seeded with a single row carrying just the sticky
    // value(s), same as a real court entry would.
    const sourceCourt = isNonEmptyVariable(selectChargeData)
      ? selectChargeData?.court
      : stickyDefaults?.court;
    if (isNonEmptyVariable(sourceCourt)) {
      const data = [];
      sourceCourt?.forEach((cl) => {
        const obj = {
          ...cl,
          uuid: uuidV4(),
          courtDateTime:
            isNonEmptyVariable(cl?.courtDate) &&
            isNonEmptyVariable(cl?.courtTime)
              ? cl?.courtDate + " " + cl?.courtTime
              : null,
        };

        data.push(obj);
      });
      setCourtListsData(data);
    } else {
      setCourtListsData([]);
    }
  }, [selectChargeData, stickyDefaults]);

  const onModalHide = () => {
    onHide();
  };

  const updateCourtListForm = (val) => {
    setCourtListsData(val);
  };

  const onSaveChanges = () => {
    chargeFormRef?.current?.form
      ?.validateFields()
      .then((values) => {
        // Split ChargesExtra dynamic fields out of the form values — they're
        // saved separately below via /api/extra/save (same generic endpoint
        // BioExtra/ArrestReportsExtra/InmatePropertyExtra use), not part of
        // the main Charges create/update payload. chargesExtraFieldKeys/
        // chargesExtraTypeMap/chargesExtraID come from AddCharges via
        // chargeFormRef — see AddCharges/index.jsx.
        const chargesExtraFieldKeys =
          chargeFormRef?.current?.chargesExtraFieldKeys || [];
        const chargesExtraTypeMap =
          chargeFormRef?.current?.chargesExtraTypeMap || {};
        const chargesExtraSnapshot = {};
        chargesExtraFieldKeys.forEach((k) => {
          const val = values?.[k];
          const htmlType = chargesExtraTypeMap[k];
          if (val) {
            chargesExtraSnapshot[k] =
              htmlType === "date"
                ? dayjs(val).format("MM/DD/YYYY")
                : htmlType === "datetime"
                  ? dayjs(val).format("MM/DD/YYYY HH:mm")
                  : val;
          } else {
            chargesExtraSnapshot[k] = null;
          }
          delete values[k];
        });

        let chargeUUID = isNonEmptyVariable(selectChargeData?.uuid)
          ? selectChargeData?.uuid
          : uuidV4();
        let bookingBailUuidv4 = isNonEmptyVariable(
          selectChargeData?.bookingBails?.[0]?.bookingBailUuid,
        )
          ? selectChargeData?.bookingBails?.[0]?.bookingBailUuid
          : uuidV4();

        if (values?.chooseBailType !== "Attach_existing_bail") {
          bookingBailUuidv4 = uuidV4();
        }

        if (isNonEmptyVariable(values?.deadlineStartDateTime)) {
          values.deadlineStartDateTime =
            values?.deadlineStartDateTime?.format("MM/DD/YYYY HH:mm");
        }

        if (isNonEmptyVariable(values?.deadlineDateTime1Local)) {
          values.deadlineDateTime1 =
            values?.deadlineDateTime1Local?.format("MM/DD/YYYY HH:mm");
        }

        if (isNonEmptyVariable(values?.deadlineDateTime2Local)) {
          values.deadlineDateTime2 =
            values?.deadlineDateTime2Local?.format("MM/DD/YYYY HH:mm");
        }

        if (isNonEmptyVariable(values?.deadlineSatisfiedDateTime1)) {
          values.deadlineSatisfiedDateTime1 =
            values?.deadlineSatisfiedDateTime1?.format("MM/DD/YYYY HH:mm");
        }

        if (isNonEmptyVariable(values?.deadlineSatisfiedDateTime2)) {
          values.deadlineSatisfiedDateTime2 =
            values?.deadlineSatisfiedDateTime2?.format("MM/DD/YYYY HH:mm");
        }

        let obj = {
          ...selectChargeData,
          ...values,
          keyID: !fullSearch ? bookings?.bookingId : null,
          creatorStaffID: !fullSearch ? user?.staffId : null,
          dispositionDate: isNonEmptyVariable(values?.dispositionDate)
            ? dayjs(values?.dispositionDate)?.format("MM/DD/YYYY")
            : null,
          sentenceDate: isNonEmptyVariable(values?.sentenceDate)
            ? dayjs(values?.sentenceDate)?.format("MM/DD/YYYY")
            : null,
          sentenceEndDate: isNonEmptyVariable(values?.sentenceEndDate)
            ? dayjs(values?.sentenceEndDate)?.format("MM/DD/YYYY")
            : null,
          offenseDate: isNonEmptyVariable(values?.offenseDateTime)
            ? dayjs(values?.offenseDateTime)?.format("MM/DD/YYYY")
            : null,
          offenseTime: isNonEmptyVariable(values?.offenseDateTime)
            ? dayjs(values?.offenseDateTime)?.format("HH:mm")
            : null,
          scheduledReleaseDate: isNonEmptyVariable(
            values?.scheduledReleaseDateTime,
          )
            ? dayjs(values?.scheduledReleaseDateTime)?.format("MM/DD/YYYY")
            : null,
          scheduledReleaseTime: isNonEmptyVariable(
            values?.scheduledReleaseDateTime,
          )
            ? dayjs(values?.scheduledReleaseDateTime)?.format("HH:mm")
            : null,
          sentenceStartDate: isNonEmptyVariable(values?.sentenceStartDateTime)
            ? dayjs(values?.sentenceStartDateTime)?.format("MM/DD/YYYY")
            : null,
          sentenceStartTime: isNonEmptyVariable(values?.sentenceStartDateTime)
            ? dayjs(values?.sentenceStartDateTime)?.format("HH:mm")
            : null,
          clearanceDate: isNonEmptyVariable(values?.clearanceDateTime)
            ? dayjs(values?.clearanceDateTime)?.format("MM/DD/YYYY")
            : null,
          clearanceTime: isNonEmptyVariable(values?.clearanceDateTime)
            ? dayjs(values?.clearanceDateTime)?.format("HH:mm")
            : null,
          deadlineDateTime1: isNonEmptyVariable(values?.deadlineDateTime1)
            ? values?.deadlineDateTime1
            : null,
          deadlineDateTime2: isNonEmptyVariable(values?.deadlineDateTime2)
            ? values?.deadlineDateTime2
            : null,
          deadlineSatisfiedDate1: isNonEmptyVariable(
            values?.deadlineSatisfiedDateTime1,
          )
            ? values?.deadlineSatisfiedDateTime1?.split(" ")?.[0]
            : null,
          deadlineSatisfiedTime1: isNonEmptyVariable(
            values?.deadlineSatisfiedDateTime1,
          )
            ? values?.deadlineSatisfiedDateTime1?.split(" ")?.[1]
            : null,
          deadlineSatisfiedDate2: isNonEmptyVariable(
            values?.deadlineSatisfiedDateTime2,
          )
            ? values?.deadlineSatisfiedDateTime2?.split(" ")?.[0]
            : null,
          deadlineSatisfiedTime2: isNonEmptyVariable(
            values?.deadlineSatisfiedDateTime2,
          )
            ? values?.deadlineSatisfiedDateTime2?.split(" ")?.[1]
            : null,
          deadlineStartDate: isNonEmptyVariable(values?.deadlineStartDateTime)
            ? values?.deadlineStartDateTime?.split(" ")?.[0]
            : null,
          deadlineStartTime: isNonEmptyVariable(values?.deadlineStartDateTime)
            ? values?.deadlineStartDateTime?.split(" ")?.[1]
            : null,
        };

        let bookingBail = null;
        if (
          (isNonEmptyVariable(selectChargeData?.bookingBailID) ||
            isNonEmptyVariable(selectChargeData?.bookingBailUuid)) &&
          isNonEmptyVariable(values?.chooseBailType) &&
          values?.chooseBailType === "Attach_existing_bail"
        ) {
          bookingBail = bookingBails?.find(
            (bail) =>
              bail?.bookingBailID === values?.bookingBailUuid ||
              bail?.bookingBailUuid === values?.bookingBailUuid,
          );
          bookingBail = {
            ...obj?.bookingBails?.[0],
            courtNumber: isNonEmptyVariable(values?.courtNumber)
              ? values?.courtNumber
              : null,
          };

          if (isNonEmptyVariable(bookingBail?.bookingBailID)) {
            obj.bookingBailID = bookingBail?.bookingBailID;
            obj.bookingID = bookings?.bookingID;
          } else {
            delete obj.bookingBailID;
          }
        }

        if (
          isNonEmptyVariable(obj?.chooseBailType) &&
          obj?.chooseBailType === "new_bail"
        ) {
          delete obj.bookingBailID;
          bookingBail = {
            ...obj?.bookingBails?.[0],
            bookingID: bookings?.bookingID,
            bookingBailUuid: bookingBailUuidv4,
            courtNumber: isNonEmptyVariable(values?.courtNumber)
              ? values?.courtNumber
              : null,
          };
        }

        if (fullSearch) {
          const payload = {
            ...obj,
            court: courtListsData,
            chargeCount: isNaN(parseInt(obj?.chargeCount))
              ? null
              : parseInt(obj?.chargeCount),
            originalChargeCount: isNaN(parseInt(obj?.originalChargeCount))
              ? null
              : parseInt(obj?.originalChargeCount),
            bookingBails: isNonEmptyVariable(bookingBail)
              ? [bookingBail]
              : null,
          };
          const prevBookings = bookings; // <-- from useSelector
          const updated = {
            ...prevBookings,
            charges: [...(prevBookings?.charges || []), payload],
          };
          dispatch(setBookings(updated));
          notifyIfAllChargesCleared(updated.charges, prevBookings?.inJail);
          onModalHide();
          return;
        }

        const payload = {
          ...obj,
          uuid: chargeUUID,
          court: courtListsData,
          chargeCount: parseInt(obj?.chargeCount),
          originalChargeCount: parseInt(obj?.originalChargeCount),
          bookingBailUuid: bookingBailUuidv4,
          bookingBails: isNonEmptyVariable(bookingBail) ? [bookingBail] : null,
        };

        let url = "";
        if (isNonEmptyVariable(selectChargeData)) {
          url = API_ENDPOINTS?.Charge_update;
        } else {
          url = API_ENDPOINTS?.Charge_create;
        }
        setisAPICall(() => true);

        // Captured before the save so we can tell, once the booking is
        // refetched below, which tracking record (if any) the Charge save
        // just created for this court date — the backend assigns the
        // InmateTrackingID as part of persisting `court[].inmateTracking`,
        // so it doesn't exist yet on `bookings.inmateTracking` here.
        const previousTrackingIds = new Set(
          (bookings?.inmateTracking || [])
            .map((t) => t?.inmateTrackingID)
            .filter(Boolean),
        );

        // Same idea for resolving the just-created charge's real chargeID
        // (needed to save ChargesExtra below) — an update already has it
        // directly from selectChargeData, but a brand new charge only gets
        // a real chargeID once the booking is refetched, and there's no
        // other way to tell which of the refetched charges is the new one.
        const previousChargeIds = new Set(
          (chargesData || []).map((c) => c?.chargeID).filter(Boolean),
        );

        // Snapshot the locally-entered extra rows: the refetch below replaces
        // `bookings` with the server's copy, which doesn't carry these
        // separately-persisted extra tables, so unsaved values would be lost.
        const bioExtraSnapshot = bookings?.bio?.[0]?.bioExtra?.[0];
        const arrestReportsExtraSnapshot =
          bookings?.arrestReports?.[0]?.arrestReportsExtra?.[0];
        const inmatePropertyExtraSnapshot =
          bookings?.inmateProperty?.[0]?.inmatePropertyExtra?.[0];
        const inmateMedicalExtraSnapshot =
          bookings?.inmateMedical?.[0]?.inmateMedicalExtra?.[0];
        const cashAcct = bookings?.bookingCashAccount?.[0]?.cashAccountingNew?.[0];
        const cashAccountingNewExtraSnapshot = cashAcct?.cashAccountingNewExtra?.[0];
        const cashAccountingNewTransactionsSnapshot =
          cashAcct?.cashAccountingNewTransactions;
        const bookingBailsSnapshot = bookings?.bookingBails;
        const holdsSnapshot = bookings?.holds;
        const movementRulesSnapshot = bookings?.inmateMovementRule;
        const inmateTrackingSnapshot = bookings?.inmateTracking;
        const inmatePropertyItemsSnapshot =
          bookings?.inmateProperty?.[0]?.inmatePropertyItems;
        const appointmentLogSnapshot = bookings?.appointmentLog;
        const bioAddressesSnapshot = bookings?.bio?.[0]?.bioAddresses;
        const scheduledEventsSnapshot = bookings?.scheduledEvents;
        const medicationsSnapshot = bookings?.medications;
        const victimNotificationsSnapshot = bookings?.victimNotifications;

        let bpayload = null;

        if (
          isNonEmptyVariable(bookings?.bookingNumber) &&
          isNonEmptyVariable(bookings?.bookingId)
        ) {
          bpayload = {
            bookings: {
              ...bookings,
              bookingId: bookings?.bookingId || null,
              bookingNumber: bookings?.bookingNumber || null,
              bio: [
                {
                  ...((bookings?.bio && bookings.bio[0]) || {}),
                  bioPhotos:
                    bookings?.bio[0]?.bioPhotos?.map((p) => {
                      return {
                        ...p,
                        lineupFlag: true,
                      };
                    }) || [],
                },
              ],
              arrestReports: bookings?.arrestReports,
            },
          };
        } else {
          bpayload = {
            bookings: {
              ...bookings,
              transactionId: irisSearchId || 0,
              creatorStaffId: user?.staffId,
              inJail: true,
              user: user?.username,
              isNeedToSubmitGenericInterface: true,
              jackets: [
                {
                  jacketId:
                    bookings?.["Folder ID"] || bookings?.jacketId || null,
                  masterTransactionDate: dayjs().format("MM/DD/YYYY HH:mm:ss"),
                  staffJacket: false,
                  orphanFlag: false,
                },
              ],
              arrestReports: [
                {
                  ...((bookings?.arrestReports && bookings.arrestReports[0]) ||
                    {}),
                  housingAgency: "Jail",
                  custodialAgency: "Jail",
                },
              ],
              bio: [
                {
                  ...((bookings?.bio && bookings.bio[0]) || {}),
                  enrolleeId:
                    bookings.bio[0]?.enrolleeId || irisEnrolleId || null,
                  bioPhotos:
                    bookings?.bio[0]?.bioPhotos?.map((p) => {
                      return {
                        ...p,
                        lineupFlag: true,
                      };
                    }) || [],
                },
              ],
            },
          };
        }

        api.post(API_ENDPOINTS.bookingsCreate, bpayload).then((resb) => {
          if (resb?.data?.statusCode === 200) {
            if (!isNonEmptyVariable(bpayload?.bookingId)) {
              payload.keyID = resb?.data?.bookingId;
            }

            api
              .post(url, payload)
              .then(async (res) => {
                if (res?.data?.statusCode === 200) {
                  const data = await getBookingsByNumberUtil(
                    resb?.data?.bookingNumber,
                  );

                  // Persist the extras (now that bio/arrestReport ids exist)
                  // BEFORE putting the refetched booking in the store, so the
                  // extra-table sync hooks load the saved values.
                  await Promise.allSettled([
                    saveBioAndArrestReportsExtra(
                      bioExtraSnapshot,
                      arrestReportsExtraSnapshot,
                      data,
                    ),
                    saveInmatePropertyExtra(inmatePropertyExtraSnapshot, data),
                    saveInmateMedicalExtra(inmateMedicalExtraSnapshot, data),
                    saveCashAccountingNewExtra(
                      cashAccountingNewExtraSnapshot,
                      data,
                    ),
                    saveCashAccountingNewTransactionsExtra(
                      cashAccountingNewTransactionsSnapshot,
                    ),
                    saveBookingBailsExtra(bookingBailsSnapshot, data),
                    saveHoldsExtra(holdsSnapshot),
                    saveInmateMovementRulesExtra(movementRulesSnapshot),
                    saveInmateTrackingExtra(inmateTrackingSnapshot),
                    saveInmatePropertyItemsExtra(inmatePropertyItemsSnapshot),
                    saveAppointmentLogExtra(appointmentLogSnapshot),
                    saveBioAddressesExtra(bioAddressesSnapshot),
                    saveBusinessesExtra(scheduledEventsSnapshot),
                    saveScheduledEventsExtra(scheduledEventsSnapshot),
                    saveMedicationLogExtra(medicationsSnapshot),
                    saveVictimNotificationExtra(victimNotificationsSnapshot),
                    saveVictimNotificationLogExtra(victimNotificationsSnapshot),
                  ]);

                  dispatch(setLastBookingsFromAPI(data));
                  dispatch(
                    setBookings({
                      ...data,
                    }),
                  );
                  notifyIfAllChargesCleared(
                    data?.charges,
                    data?.inJail ?? bookings?.inJail,
                  );

                  // Resolve the real chargeID and save ChargesExtra —
                  // an update already has it directly from
                  // selectChargeData; a brand new charge only gets one once
                  // the booking above is refetched, found here by
                  // diffing against previousChargeIds (same technique as
                  // the transport-triggering diff right below).
                  if (Object.keys(chargesExtraSnapshot).length > 0) {
                    const resolvedChargeID =
                      selectChargeData?.chargeID ??
                      (data?.charges || []).find(
                        (c) =>
                          c?.chargeID && !previousChargeIds.has(c.chargeID),
                      )?.chargeID;

                    if (resolvedChargeID) {
                      saveExtraTableRecord({
                        tableName: "ChargesExtra",
                        idColumn: "chargeID",
                        idValue: resolvedChargeID,
                        extraRecord: {
                          ...chargesExtraSnapshot,
                          chargesExtraID:
                            chargeFormRef?.current?.chargesExtraID ?? 0,
                        },
                        extraIdKey: "chargesExtraID",
                      }).catch((error) => {
                        console.error(
                          "[EditChargesModal] ChargesExtra save failed:",
                          error,
                        );
                      });
                    }
                  }

                  // Mirrors legacy CourtInterface.asp/SynchUpWithInmateTracking:
                  // scheduling a court date creates a Loan/Court movement, which
                  // auto-creates the outbound + return Transport records.
                  const newTrackingEntries = getTriggeringEntriesFromDiff(
                    data?.inmateTracking,
                    previousTrackingIds,
                  );
                  if (newTrackingEntries.length) {
                    const context = {
                      jacketID: data?.jacketId,
                      bioID: data?.bioId,
                      creatorStaffID: user?.staffId,
                      housingAgency: user?.staff?.organization?.[0]?.agency,
                      jackets: data?.jackets,
                      bio: data?.bio,
                      cdInvolvements: data?.cdInvolvements,
                    };
                    const transportPayloads = newTrackingEntries.flatMap(
                      (entry) => buildTransportPayloads(entry, context),
                    );
                    if (transportPayloads.length) {
                      createTransportsForNewMovements(transportPayloads);
                    }
                  }
                  // if (isNonEmptyVariable(selectChargeData)) {
                  //   notificationService.success(
                  //     "Success!",
                  //     "Charges data updated successfully"
                  //   );
                  // } else {
                  //   notificationService.success(
                  //     "Success!",
                  //     "Charge data created successfully"
                  //   );
                  // }

                  onModalHide();
                } else {
                  if (isNonEmptyVariable(selectChargeData)) {
                    notificationService.error(
                      "Failed!",
                      "Charge data updated failed",
                    );
                  } else {
                    notificationService.error(
                      "Failed!",
                      "Charge data created failed",
                    );
                  }
                }
              })
              .catch((err) => {
                console.log(err);
                if (isNonEmptyVariable(selectChargeData)) {
                  notificationService.error(
                    "Failed!",
                    "Charge data updated failed",
                  );
                } else {
                  console.log("Failed to create booking");
                  notificationService.error(
                    "Failed!",
                    "Charge data created failed",
                  );
                }
              })
              .finally(() => {
                setisAPICall(() => false);
              });
          }
        });
      })
      .catch((errorInfo) => {
        chargeFormRef?.current?.form?.scrollToField(
          errorInfo?.errorFields?.[0]?.name,
          {
            behavior: "smooth",
            block: "center",
          },
        );
      });
  };

  useEffect(() => {}, [chargesData]);
  useEffect(() => {
    if (isEdit) {
      let showEdit = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.MODIFY,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report,
      );
      if (showEdit) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    } else {
      setDisabled(false);
    }
  }, [isEdit]);
  return (
    <>
      <Modal
        designPattern="ant"
        show={show}
        onHide={onModalHide}
        header="Charges"
        width="75%"
        handleSubmit={onSaveChanges}
        handleCancel={onModalHide}
        loading={isAPICall}
        scrollable
        hideSubmitButton={isDisabled}
      >
        <AddCharges
          formData={selectChargeData || stickyDefaults}
          isEdit={isEdit}
          chargeFormRef={chargeFormRef}
          bookingBails={bookingBails}
          updateCourtListForm={updateCourtListForm}
          courtListsData={courtListsData}
          chargesData={chargesData}
          startingPoint={startingPoint}
        />
      </Modal>
    </>
  );
};

export default EditChargesModal;
